import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import Loader from './components/Loader';

// Pages
import Login from './pages/Login';
import CustomerLogin from './pages/CustomerLogin';
import Home from './pages/Home';
import QuickAdd from './pages/QuickAdd';
import CustomerDetail from './pages/CustomerDetail';
import AddOperation from './pages/AddOperation';
import AddPet from './pages/AddPet';
import Records from './pages/Records';
import Cash from './pages/Cash';
import Admin from './pages/Admin';
import Landing from './pages/Landing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import KVKKPage from './pages/KVKKPage';
import CustomerPanel from './pages/CustomerPanel';
import TicketForm from './pages/TicketForm';
import Customers from './pages/Customers';
import Pets from './pages/Pets';
import Boarding from './pages/Boarding';
import SettingsPage from './pages/Settings';
import Stock from './pages/Stock';
import Notifications from './pages/Notifications';
import Calendar from './pages/Calendar';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ClinicLayout from './components/ClinicLayout';

import './App.css';

// Protected Route for Staff (vet/admin)
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin, isCustomer } = useAuth();

  if (loading) {
    return <Loader fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Customers should not access staff routes
  if (isCustomer()) {
    return <Navigate to="/customer-panel" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/panel" replace />;
  }

  return children;
};

// Protected Route for Customers only
const CustomerRoute = ({ children }) => {
  const { user, loading, isCustomer } = useAuth();

  if (loading) {
    return <Loader fullScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isCustomer()) {
    return <Navigate to="/panel" replace />;
  }

  return children;
};

// Landing with auto-redirect if logged in
const LandingRoute = ({ children }) => {
  const { user, loading, isCustomer, isAdmin } = useAuth();

  if (loading) {
    return <Loader fullScreen />;
  }

  if (user) {
    if (isCustomer()) return <Navigate to="/customer-panel" replace />;
    if (isAdmin()) return <Navigate to="/admin" replace />;
    return <Navigate to="/panel" replace />;
  }

  return children;
};

// Public Route (redirect to appropriate home if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, isCustomer, isAdmin } = useAuth();

  if (loading) {
    return <Loader fullScreen />;
  }

  if (user) {
    if (isCustomer()) return <Navigate to="/customer-panel" replace />;
    if (isAdmin()) return <Navigate to="/admin" replace />;
    return <Navigate to="/panel" replace />;
  }

  return children;
};

// Protected Route with Clinic Layout (sidebar on desktop)
const ClinicRoute = ({ children }) => {
  const { user, loading, isAdmin, isCustomer } = useAuth();

  if (loading) return <Loader fullScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (isCustomer()) return <Navigate to="/customer-panel" replace />;
  if (isAdmin()) return <Navigate to="/admin" replace />;

  return <ClinicLayout>{children}</ClinicLayout>;
};

// Permission-gated Clinic Route
const PermRoute = ({ perm, children }) => {
  const { user, loading, isAdmin, isCustomer, hasPermission } = useAuth();

  if (loading) return <Loader fullScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (isCustomer()) return <Navigate to="/customer-panel" replace />;
  if (isAdmin()) return <Navigate to="/admin" replace />;
  if (perm && !hasPermission(perm)) return <Navigate to="/panel" replace />;

  return <ClinicLayout>{children}</ClinicLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page - Root */}
      <Route path="/" element={
        <LandingRoute>
          <Landing />
        </LandingRoute>
      } />
      
      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/customer-login"
        element={
          <PublicRoute>
            <CustomerLogin />
          </PublicRoute>
        }
      />

      {/* Customer Panel */}
      <Route
        path="/customer-panel"
        element={
          <CustomerRoute>
            <CustomerPanel />
          </CustomerRoute>
        }
      />

      {/* Staff Protected Routes - Main pages with sidebar */}
      <Route path="/panel" element={<ClinicRoute><Home /></ClinicRoute>} />
      <Route path="/customers" element={<PermRoute perm="customers"><Customers /></PermRoute>} />
      <Route path="/pets" element={<PermRoute perm="customers"><Pets /></PermRoute>} />
      <Route path="/boarding" element={<PermRoute perm="boarding"><Boarding /></PermRoute>} />
      <Route path="/stock" element={<PermRoute perm="stock"><Stock /></PermRoute>} />
      <Route path="/records" element={<PermRoute perm="reports"><Records /></PermRoute>} />
      <Route path="/cash" element={<PermRoute perm="reports"><Cash /></PermRoute>} />
      <Route path="/settings" element={<PermRoute perm="settings"><SettingsPage /></PermRoute>} />
      <Route path="/calendar" element={<PermRoute perm="appointments"><Calendar /></PermRoute>} />
      <Route path="/notifications" element={<ClinicRoute><Notifications /></ClinicRoute>} />
      
      {/* Staff sub-pages without sidebar */}
      <Route path="/quick-add" element={<ProtectedRoute><QuickAdd /></ProtectedRoute>} />
      <Route path="/customer/:id" element={<ProtectedRoute><CustomerDetail /></ProtectedRoute>} />
      <Route path="/add-operation" element={<ProtectedRoute><AddOperation /></ProtectedRoute>} />
      <Route path="/add-pet" element={<ProtectedRoute><AddPet /></ProtectedRoute>} />
      <Route path="/tickets" element={<ProtectedRoute><TicketForm /></ProtectedRoute>} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Admin />
          </ProtectedRoute>
        }
      />

      {/* Legal Pages */}
      <Route path="/gizlilik" element={<PrivacyPolicy />} />
      <Route path="/kvkk" element={<KVKKPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
      document.querySelectorAll('a[href*="emergent.sh"]').forEach(el => {
        if (el.textContent && el.textContent.includes('Made with')) el.remove();
      });
    };
    removeBadge();
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.id === 'emergent-badge' || 
              (node.tagName === 'A' && node.href && node.href.includes('emergent.sh'))) {
            node.remove();
          }
        }
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
          <PWAInstallPrompt />
          <Toaster 
            position="top-center" 
            richColors
            swipeDirections={['right']}
            toastOptions={{
              style: {
                borderRadius: '16px',
                padding: '16px',
              },
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
